import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ItemVisual } from '../components/ItemVisual';
import { audio } from '../core/audio';
import { useActivity } from './useActivity';

export function WhatDisappeared({ activity, onComplete }) {
  const [phase, setPhase] = useState('show'); // show | hidden | ask
  const [missing] = useState(() => activity.items[Math.floor(Math.random() * activity.items.length)]);
  const { state, answer, showHint } = useActivity(activity, onComplete);

  useEffect(() => {
    let alive = true;
    (async () => {
      await audio.playVoice('mem_look');
      await new Promise((r) => setTimeout(r, activity.showMs || 2500));
      if (!alive) return;
      setPhase('hidden');
      await new Promise((r) => setTimeout(r, 1200));
      if (!alive) return;
      setPhase('ask');
      audio.playVoice(activity.voice);
    })();
    return () => { alive = false; };
  }, [activity.id]); // eslint-disable-line

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-8 px-5" data-testid={`activity-${activity.id}`}>
      <AnimatePresence mode="wait">
        {phase === 'show' && (
          <motion.div key="show" exit={{ opacity: 0 }} className="flex gap-6">
            {activity.items.map((it) => (
              <div key={it.id} className="rounded-[2rem] p-3 bg-white/80 backdrop-blur" style={{ border: '5px solid #fff' }}>
                <ItemVisual item={it} size={110} />
              </div>
            ))}
          </motion.div>
        )}
        {phase === 'hidden' && (
          <motion.div key="hide" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="w-64 h-40 rounded-[2rem] bg-lavender/80 backdrop-blur flex items-center justify-center text-5xl">
            <motion.div animate={{ rotate: [0, 8, -8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>☁️</motion.div>
          </motion.div>
        )}
        {phase === 'ask' && (
          <motion.div key="ask" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-8">
            <div className="flex gap-6 opacity-90">
              {activity.items.filter((it) => it.id !== missing.id).map((it) => (
                <div key={it.id} className="rounded-[2rem] p-3 bg-white/80" style={{ border: '5px solid #fff' }}>
                  <ItemVisual item={it} size={90} />
                </div>
              ))}
              <div className="rounded-[2rem] p-3 bg-white/40 border-4 border-dashed border-white w-[114px] h-[114px] flex items-center justify-center text-4xl text-navy/40">?</div>
            </div>
            <div className="flex gap-5">
              {activity.items.map((it) => {
                const isCorrect = it.id === missing.id;
                return (
                  <motion.button key={it.id} data-testid={`choice-${it.id}`} whileTap={{ scale: 0.92 }}
                    animate={state === 'correct' && isCorrect ? { scale: [1, 1.15, 1.08] } : showHint && isCorrect ? { scale: [1, 1.06, 1] } : {}}
                    transition={showHint && isCorrect ? { repeat: Infinity, duration: 1.2 } : {}}
                    onClick={() => answer(isCorrect)}
                    className="rounded-[2rem] p-3 bg-white/85 backdrop-blur min-w-[96px] min-h-[96px]"
                    style={{ border: '5px solid #fff', boxShadow: state === 'correct' && isCorrect ? '0 0 0 6px #FFD166' : '0 10px 24px rgba(29,53,87,0.15)' }}>
                    <ItemVisual item={it} size={92} />
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
